import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BRAND, SOCIAL } from '@/lib/constants';

interface Column {
  title: string;
  links: { label: string; to: string }[];
}

// Footer taxonomy preserved from the WordPress theme (footer.php).
const COLUMNS: Column[] = [
  {
    title: 'Gold & Items',
    links: [
      { label: 'Buy OSRS Gold', to: '/services/gold' },
      { label: 'Sell OSRS Gold', to: '/services/gold' },
      { label: 'Buy OSRS Items', to: '/services/items' },
      { label: 'Buy OSRS Bonds', to: '/services/items' },
    ],
  },
  {
    title: 'Elite Upgrades',
    links: [
      { label: 'Inferno — Infernal Cape', to: '/search?q=inferno' },
      { label: "Colosseum — Dizana's Quiver", to: '/search?q=colosseum' },
      { label: 'Fight Caves — Fire Cape', to: '/search?q=fire%20cape' },
    ],
  },
  {
    title: 'End-Game PvM',
    links: [
      { label: 'Yama', to: '/services/pvm?search=yama' },
      { label: 'Megascales', to: '/services/pvm?search=megascale' },
      { label: 'Combat Achievements', to: '/services/combat-achievements' },
      { label: 'Awakened Bosses', to: '/services/pvm?search=awakened' },
      { label: 'Doom', to: '/services/pvm?search=doom' },
    ],
  },
  {
    title: 'Guaranteed Purple',
    links: [
      { label: 'CoX until Purple', to: '/services/pvm?search=chambers' },
      { label: 'ToB until Purple', to: '/services/pvm?search=theatre' },
      { label: 'ToA until Purple', to: '/services/pvm?search=tombs' },
    ],
  },
  {
    title: 'Bossing',
    links: [
      { label: 'General Bosses', to: '/services/pvm' },
      { label: 'Chambers of Xeric', to: '/services/pvm?search=chambers' },
      { label: 'Theatre of Blood', to: '/services/pvm?search=theatre' },
      { label: 'Tombs of Amascut', to: '/services/pvm?search=tombs' },
    ],
  },
  {
    title: 'Skilling & Gathering',
    links: [
      { label: 'Skilling', to: '/services/skilling' },
      { label: 'Gathering & Misc', to: '/services/ironman-gathering' },
    ],
  },
  {
    title: 'Content Unlocks',
    links: [
      { label: 'Quests', to: '/services/quests' },
      { label: 'Diaries', to: '/services/diaries' },
      { label: 'Minigames & Outfits', to: '/services/minigames' },
    ],
  },
];

export function Footer() {
  return (
    <footer className="mt-20 border-t border-border bg-card/40">
      <div className="container py-12">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-7">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="mb-3 text-sm font-semibold text-foreground">{col.title}</h3>
              <ul className="space-y-2">
                {col.links.map((l) => (
                  <li key={`${col.title}-${l.label}`}>
                    <Link to={l.to} className="text-sm text-muted-foreground hover:text-primary">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col gap-6 border-t border-border pt-8 md:flex-row md:items-end md:justify-between">
          <div>
            <Link to="/" className="font-display text-2xl text-primary glow-text">
              {BRAND.name}
            </Link>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">{BRAND.tagline}</p>
            <div className="mt-3 flex items-center gap-3">
              <SocialLink href={SOCIAL.twitter} label="Firstseller on X (Twitter)">
                <XIcon />
              </SocialLink>
              <SocialLink href={SOCIAL.instagram} label="Firstseller on Instagram">
                <InstagramIcon />
              </SocialLink>
              <SocialLink href={SOCIAL.tiktok} label="Firstseller on TikTok">
                <TikTokIcon />
              </SocialLink>
              <SocialLink href={SOCIAL.discord} label="Firstseller on Discord">
                <DiscordIcon />
              </SocialLink>
            </div>
          </div>
          <div className="flex gap-12">
            <div>
              <h4 className="mb-2 text-sm font-semibold">Resources</h4>
              <ul className="space-y-1">
                <li>
                  <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-muted-foreground hover:text-primary">
                    Contact Support
                  </Link>
                </li>
                <li>
                  <Link to="/reviews" className="text-sm text-muted-foreground hover:text-primary">
                    Reviews
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="mb-2 text-sm font-semibold">Legal</h4>
              <ul className="space-y-1">
                <li>
                  <Link to="/legal/terms" className="text-sm text-muted-foreground hover:text-primary">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/legal/privacy" className="text-sm text-muted-foreground hover:text-primary">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/legal/refund" className="text-sm text-muted-foreground hover:text-primary">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} {BRAND.name}. All rights reserved. {BRAND.disclaimer}
        </p>
      </div>
    </footer>
  );
}

function SocialLink({ href, label, children }: { href: string; label: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="grid h-9 w-9 place-items-center rounded-full border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary"
    >
      {children}
    </a>
  );
}

// Brand glyphs (single-path, currentColor) — kept inline so there's no extra icon dependency.
function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

function DiscordIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}
